import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { Unsubscribe } from "firebase/auth";
import {
  collection,
  doc as firestoreDoc,
  onSnapshot,
  query,
  Timestamp,
} from "firebase/firestore";

import { firestore } from "../core";
import { useAuth } from "../core/providers/auth";
import { FormatDate } from "../hooks/timestamp";
import { PublicUser } from "./users";

interface WorkoutsContext {
  loading: boolean;
  error: string | null;
  workoutsOnDate: WorkoutsOnDate[];
  workouts: Workout[];
  workoutDates: Date[];
}

export type Participant = {
  uid: string;
  displayName: string;
  email: string;
  phone: string;
  photoURL?: string;
  creditsCharged?: number;
  checkedIn?: boolean;
  checkedInAt?: Timestamp;
  checkedInBy?: string;
  checkedInByName?: string;
};

export type WaitlistEntry = {
  uid: string;
  displayName: string;
  email: string;
  phone: string;
  createdAt: Timestamp;
};

export interface Workout {
  id: string;
  recurrenceSeriesId?: string;
  recurrenceIndex?: number;
  recurrenceCount?: number;
  admin: {
    uid: string;
    displayName: string;
    email: string;
    phone: string;
    photoURL: string;
  };
  type: string;
  body: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  date: Timestamp;
  startTime: Timestamp;
  endTime: Timestamp;
  location: string;
  credits: number;
  collectionTime: number;
  registrationTime: number;
  gear: string[];
  numberOfPlaces: number;
  participantsCount: number;
  participantsList: Participant[];
  waitlistCount: number;
  waitlist: WaitlistEntry[];
  coaches: PublicUser[];
  title: string;
}

interface WorkoutsOnDate {
  date: Date
  workouts: Workout[]
}

const Workouts = createContext({} as WorkoutsContext);

export default function WorkoutsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { user, admin, coach } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [workoutDates, setWorkoutDates] = useState<Date[]>([]);
  const [workoutsOnDate, setWorkoutsOnDate] = useState<WorkoutsOnDate[]>([])

  const displayTimeSpan = new Date();
  displayTimeSpan.setHours(displayTimeSpan.getHours() - 12);

  const setWorkoutsOnDateArray = (arr: Workout[]) => {
    let wdArr: WorkoutsOnDate[] = []
    let dateArr = arr.map((w) => new Date(FormatDate(w.startTime.toDate())))

    dateArr = dateArr
      .map((date) => date.getTime())
      .filter((date, i, array) => array.indexOf(date) === i)
      .map((time) => new Date(time))

    dateArr.forEach((d) => wdArr.push({ date: d, workouts: [] }))
    wdArr.forEach((wd) => {
      arr.forEach((w) => {
        if (FormatDate(w.startTime.toDate()) === FormatDate(wd.date)) {
          wd.workouts.push(w)
        }
      })
      wd.workouts.sort((a, b) => (a.startTime > b.startTime ? 1 : -1))
    })

    wdArr.sort((a, b) => (a.date > b.date ? 1 : -1))
    return wdArr
  }

  const getWorkoutDates = (arr: Workout[]) =>
    arr
      .filter(
        (item, index, array) =>
          index ===
          array.findIndex(
            (w) =>
              FormatDate(item.startTime.toDate()) ===
                FormatDate(w.startTime.toDate()) &&
              w.startTime.toDate() > displayTimeSpan
          )
      )
      .sort((a, b) => (a.startTime > b.startTime ? 1 : -1))
      .map((i) => i.startTime.toDate());

  const updateWorkoutCollections = (arr: Workout[]) => {
    setWorkouts(arr);
    setWorkoutDates(getWorkoutDates(arr));
    setWorkoutsOnDate(setWorkoutsOnDateArray(arr));
  };

  const mapParticipant = (id: string, data: Record<string, any>): Participant => ({
    uid: id,
    displayName: data.displayName ?? data.email ?? '',
    email: data.email ?? '',
    phone: data.phone ?? '',
    photoURL: data.photoURL ?? '',
    creditsCharged: data.creditsCharged ?? 0,
    checkedIn: data.checkedIn ?? false,
    checkedInAt: data.checkedInAt,
    checkedInBy: data.checkedInBy,
    checkedInByName: data.checkedInByName,
  });

  const mapWaitlistEntry = (
    id: string,
    data: Record<string, any>
  ): WaitlistEntry => ({
    uid: id,
    displayName: data.displayName ?? data.email ?? '',
    email: data.email ?? '',
    phone: data.phone ?? '',
    createdAt: data.createdAt ?? Timestamp.fromMillis(0),
  });

  useEffect(() => {
    let unsub: Unsubscribe;
    let participantUnsubs: Unsubscribe[] = [];
    let waitlistUnsubs: Unsubscribe[] = [];
    let workoutCache: Workout[] = [];
    let q;
    setLoading(true);

    if (user) {
      if (admin) q = collection(firestore, "/workouts");
      else q = query(collection(firestore, "/workouts"));

      setWorkouts([]);
      setWorkoutsOnDate([]);

      unsub = onSnapshot(
        q,
        (snapshot) => {
          participantUnsubs.forEach((unsubParticipants) => unsubParticipants());
          participantUnsubs = [];
          waitlistUnsubs.forEach((unsubWaitlist) => unsubWaitlist());
          waitlistUnsubs = [];

          const arr: Workout[] = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            arr.push({
              id: doc.id,
              ...data,
              participantsCount: data.participantsCount ?? 0,
              participantsList: data.participantsList ?? [],
              waitlistCount: data.waitlistCount ?? 0,
              waitlist: data.waitlist ?? [],
            } as Workout);
          });

          workoutCache = arr;
          updateWorkoutCollections(arr);
          setLoading(false);

          snapshot.forEach((workoutDoc) => {
            const unsubParticipants = onSnapshot(
              collection(firestore, 'workouts', workoutDoc.id, 'participants'),
              (participantsSnapshot) => {
                const participantsList = participantsSnapshot.docs.map(
                  (participantDoc) =>
                    mapParticipant(participantDoc.id, participantDoc.data())
                );

                workoutCache = workoutCache.map((workout) =>
                  workout.id === workoutDoc.id
                    ? {
                        ...workout,
                        participantsCount: participantsSnapshot.size,
                        participantsList,
                      }
                    : workout
                );

                updateWorkoutCollections(workoutCache);
              },
              (error) => {
                console.log(error.message);
                setError(error.message);
              }
            );

            participantUnsubs.push(unsubParticipants);

            const updateWorkoutWaitlist = (waitlist: WaitlistEntry[]) => {
              workoutCache = workoutCache.map((workout) =>
                workout.id === workoutDoc.id
                  ? {
                      ...workout,
                      waitlistCount: waitlist.length,
                      waitlist,
                    }
                  : workout
              );

              updateWorkoutCollections(workoutCache);
            }

            const unsubWaitlist =
              admin || coach
                ? onSnapshot(
                    collection(firestore, 'workouts', workoutDoc.id, 'waitlist'),
                    (waitlistSnapshot) => {
                      const waitlist = waitlistSnapshot.docs
                        .map((waitlistDoc) =>
                          mapWaitlistEntry(waitlistDoc.id, waitlistDoc.data())
                        )
                        .sort((a, b) => {
                          return (
                            a.createdAt.toMillis() - b.createdAt.toMillis()
                          )
                        })

                      updateWorkoutWaitlist(waitlist)
                    },
                    (error) => {
                      console.log(error.message);
                      setError(error.message);
                    }
                  )
                : onSnapshot(
                    firestoreDoc(
                      firestore,
                      'workouts',
                      workoutDoc.id,
                      'waitlist',
                      user.uid
                    ),
                    (waitlistDoc) => {
                      updateWorkoutWaitlist(
                        waitlistDoc.exists()
                          ? [
                              mapWaitlistEntry(
                                waitlistDoc.id,
                                waitlistDoc.data()
                              ),
                            ]
                          : []
                      )
                    },
                    (error) => {
                      console.log(error.message);
                      setError(error.message);
                    }
                  );

            waitlistUnsubs.push(unsubWaitlist);
          });

        },
        (error) => {
          console.log(error.message);
          setLoading(false);
          setError(error.message);
        }
      );
    }

    return () => {
      unsub && unsub();
      participantUnsubs.forEach((unsubParticipants) => unsubParticipants());
      waitlistUnsubs.forEach((unsubWaitlist) => unsubWaitlist());
      setWorkouts([]);
      setWorkoutsOnDate([])
    };
    // eslint-disable-next-line
  }, [user, admin, coach]);

  return (
    <Workouts.Provider
      value={{
        loading,
        error,
        workoutsOnDate,
        workouts,
        workoutDates,
      }}>
      {children}
    </Workouts.Provider>
  );
}

export const useWorkouts = () => useContext(Workouts);
